import { FC, ReactNode } from 'react';
import styled, { keyframes } from 'styled-components';
import {
	StyledInputMaskProps,
	StyledInputProps,
	StyledInputWrapperProps,
	StyledPlaceholerProps,
	StyledIconProps
} from './input.types';

const onAutoFillStart = () => keyframes`
	0% {}
	100% {}
`;
const onAutoFillCancel = () => keyframes`
	0% {}
	100% {}
`;

export const StyledFieldset = styled.fieldset`
	position: relative;
	width: 100%;
`;

export const StyledInputWrapper = styled.div<StyledInputWrapperProps>`
	position: relative;

	&:before {
		position: absolute;
		top: 23px;
		left: 12px;
		font: var(--heading-5);
		color: ${props => (props.focused ? 'rgb(var(--input-font-focus-color))' : 'rgb(var(--input-font-color))')};
		content: ${props =>
			props.visible && props.extraSign && typeof props.extraSign === 'string' ? `'${props.extraSign}'` : ''};
	}
`;

export const StyledAmountInput = styled.input<{ hasError: boolean }>`
	font: var(--h1);
	min-height: 64px;
	padding: 14px 0;
	outline: none;
	line-height: 1;
	text-align: center;
	width: 100%;

	color: rgba(var(--input-font-color), 1);
	border-radius: 3px 3px 0px 0px;
	border-bottom: 1px solid rgba(var(--input-border-color), 1);
	background-color: rgba(var(--input-background-color), 1);

	border-bottom-color: ${props =>
		props.hasError ? 'rgb(var(--input-border-error-color))' : 'rgb(var(--input-border-color))'};

	&:focus {
		color: rgba(var(--input-font-focus-color), 1);
		border-bottom: 2px solid rgba(var(--input-border-focus-color), 1);

		border-bottom-color: ${props =>
			props.hasError ? 'rgb(var(--input-border-error-color))' : 'rgb(var(--input-border-focus-color))'};
	}

	&::placeholder {
		color: rgba(var(--input-placeholder-color), 1);
	}
`;

export const StyledInput = styled.input<StyledInputProps>`
	width: 100%;
	height: 56px;

	border-radius: 3px 3px 0px 0px;
	background-color: rgb(var(--input-background-color));

	color: ${props => (props.valueHidden ? 'transparent' : 'rgb(var(--input-font-color))')};

	font: var(--heading-5);

	border-bottom-width: 1px;
	border-bottom-style: solid;
	border-bottom-color: ${props =>
		props.validationMessage ? 'rgb(var(--input-border-error-color))' : 'rgb(var(--input-border-color))'};

	padding: 0 12px;
	padding-top: 24px;
	padding-bottom: 9px;

	transition: border-bottom 0.2s;

	padding-left: ${props => (props.extraSign && !props.noPadding ? '24px' : '12px')};

	&:hover {
		border-bottom-color: ${props =>
			props.validationMessage ? 'rgb(var(--input-border-error-color))' : 'rgb(var(--input-border-hover-color))'};
	}

	&:focus {
		outline: none;
		color: ${props => (props.valueHidden ? 'transparent' : 'rgb(var(--input-font-focus-color))')};
		caret-color: ${props =>
			props.valueHidden
				? 'transparent'
				: 'rgb(var(--new-qd--amount-input-caret-color, var(--input-font-focus-color)))'};
		border-bottom-width: 2px;
		border-bottom-color: ${props =>
			props.validationMessage ? 'rgb(var(--input-border-error-color))' : 'rgb(var(--input-border-focus-color))'};
	}

	&:disabled {
		border-bottom-color: rgb(var(--input-border-disabled-color));
		background-color: rgb(var(--input-disabled-background-color));
		color: rgb(var(--input-font-disabled-color));

		&:hover {
			color: rgb(var(--input-font-disabled-color));
			border-bottom-color: rgb(var(--input-border-disabled-color));
		}
	}
	&:-webkit-autofill {
		animation-name: ${onAutoFillStart()};
		transition: background-color 50000s ease-in-out 0s;
	}

	&:not(:-webkit-autofill) {
		animation-name: ${onAutoFillCancel()};
	}

	&:-webkit-autofill,
	&:-webkit-autofill:hover {
		-webkit-text-fill-color: rgb(var(--input-font-color)) !important;
		box-shadow: inset 0 0 0 9999px rgb(var(--input-background-color)) !important;
	}

	&:-webkit-autofill:focus {
		-webkit-text-fill-color: rgb(var(--input-font-focus-color)) !important;
		box-shadow: inset 0 0 0 9999px rgb(var(--input-background-color)) !important;
	}
	// only safari
	&::-webkit-credentials-auto-fill-button,
	&::-webkit-contacts-auto-fill-button {
		background-color: rgb(var(--input-border-hover-color));
		border-radius: 5px;
		margin-right: ${props => (props.validationMessage ? '30px' : '0')};
		margin-bottom: 12px;
	}
	&#password::-webkit-credentials-auto-fill-button,
	&#password::-webkit-contacts-auto-fill-button {
		margin-right: 0;
	}
	&[data-at='input-loginPassword']::-webkit-contacts-auto-fill-button,
	&[data-at='input-loginPassword']::-webkit-credentials-auto-fill-button,
	&[data-at='input-password']::-webkit-credentials-auto-fill-button,
	&[data-at='input-password']::-webkit-contacts-auto-fill-button,
	&[data-at='input-passwordConfirmation']::-webkit-credentials-auto-fill-button,
	&[data-at='input-passwordConfirmation']::-webkit-contacts-auto-fill-button {
		position: absolute;
		right: 48px;
		bottom: 16px;
		margin-bottom: 0;
	}
	&:not(:-webkit-autofill)[data-at='input-loginPassword'],
	&:not(:-webkit-autofill)[data-at='input-password'],
	&:not(:-webkit-autofill)[data-at='input-passwordConfirmation'] {
		padding-left: '12px';
	}
`;

export const StyledLabelPlaceholder = styled.label<StyledPlaceholerProps>`
	position: absolute;
	top: ${props => (props.top ? '7px' : '16px')};
	left: 12px;
	transition: top 0.2s;
	pointer-events: none;
	width: 90%;
	max-width: 90%;
	overflow: hidden;
	white-space: nowrap;
	text-overflow: ellipsis;

	font: ${props => (props.top ? 'var(--caption)' : 'var(--heading-5)')};
	color: ${props =>
		props.disabled ? 'rgb(var(--input-placeholder-disabled-color))' : 'rgb(var(--input-placeholder-color))'};
`;

export const StyledPlaceholer = styled.span<StyledPlaceholerProps>`
	position: absolute;
	top: ${props => (props.top ? '7px' : '16px')};
	left: 12px;
	transition: top 0.2s;
	pointer-events: none;
	width: 90%;
	max-width: 90%;
	overflow: hidden;
	white-space: nowrap;
	text-overflow: ellipsis;

	font: ${props => (props.top ? 'var(--caption)' : 'var(--heading-5)')};
	color: ${props =>
		props.disabled ? 'rgb(var(--input-placeholder-disabled-color))' : 'rgb(var(--input-placeholder-color))'};
`;

export const StyledWarning = styled.div`
	margin-top: 4px;
	margin-left: 12px;
	font: var(--caption);
	color: rgb(var(--input-error-color));
`;

export const StyledInputMask = styled.div<StyledInputMaskProps>`
	position: absolute;
	height: 100%;
	padding: 0 12px;
	padding-top: 24px;
	padding-bottom: 9px;
	color: ${({ focused }) => (focused ? 'rgb(var(--input-font-focus-color))' : 'rgb(var(--input-font-color))')};
	font: var(--heading-5);
	pointer-events: none;

	&:-webkit-autofill {
		-webkit-text-fill-color: rgb(var(--input-font-color)) !important;
		box-shadow: none !important;
	}
`;

export const StyledIcon = styled.svg<StyledIconProps>`
	height: 56px;
	width: 20px !important;
	bottom: auto;
	right: 18px;
	top: 0;

	@keyframes shake {
		0% {
			animation-timing-function: ease-in;
			opacity: 1;
			transform: translateX(24px);
		}

		24% {
			opacity: 1;
		}

		40% {
			animation-timing-function: ease-in;
			transform: translateX(12px);
		}

		65% {
			animation-timing-function: ease-in;
			transform: translateX(7px);
		}

		82% {
			animation-timing-function: ease-in;
			transform: translateX(3px);
		}

		93% {
			animation-timing-function: ease-in;
			transform: translateX(2px);
		}

		25%,
		55%,
		75%,
		87%,
		98% {
			animation-timing-function: ease-out;
			transform: translateX(0px);
		}

		100% {
			animation-timing-function: ease-out;
			opacity: 1;
			transform: translateX(0px);
		}
	}
	animation: ${props => (props.shake ? 'shake 0.3s cubic-bezier(0.11, 0, 0.5, 0) 0s 1 normal forwards' : '')};
`;
// For storybook
const StyledWrapper = styled.div`
	width: 351px;
	max-width: calc(100vw - 20px);
	background-color: var(--sidebar-bg, rgba(var(--base-11), 1));
	height: calc(100vh - 20px);
	padding: 12px;
	display: flex;
	align-items: center;
`;

interface Props {
	children?: ReactNode | ReactNode[];
}
export const Wrapper: FC<Props> = ({ children }) => (
	<StyledWrapper id="input-wrapper-storybook">{children}</StyledWrapper>
);
